import { ReactElement, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Button from "../../components/staff/Button";
import User from "../../components/Atomos/Icons/User";
import Drag_pan from "../../components/Atomos/Icons/Drag_pan";
import ProfilePhoto from "../../components/Atomos/ProfilePhoto";
import Email from "../../components/Atomos/Icons/Email";
import Trash from "../../components/Atomos/Icons/EditMode/Trash";
import SaveChanges from "../../components/Atomos/Icons/EditMode/SaveChanges";
import { Cancel } from "../../components/Atomos/Icons/Cancel";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import {
  getWsgContactAdmin,
  saveWsgContactAdmin,
  type WsgContactAdminCard,
} from "../../Services/Person";
import { resolveProfilePictureDisplayUrl } from "../../utils/profilePictureUrl";
import { buildPersonProfilePath } from "../../utils/publicProfileUrls";
import { WORLD_SERVICES_GROUP_FIRM_ID } from "../../utils/wsgStaffFirms";
import { toastError, toastSuccess } from "../../utils/toast";
import ContactTopicCatalog from "./ContactTopicCatalog";

function sameOrder(left: WsgContactAdminCard[], right: WsgContactAdminCard[]): boolean {
  return left.map((person) => person.id).join("|") === right.map((person) => person.id).join("|");
}

function StaffCard({
  person,
  onRemove,
}: {
  person: WsgContactAdminCard;
  onRemove: (id: string) => void;
}): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: person.id,
  });
  const fullName = `${person.name} ${person.lastName}`.trim();
  const profilePath =
    person.generatedId != null
      ? `/ManageFirm/Professionals/${WORLD_SERVICES_GROUP_FIRM_ID}/Edit/${person.generatedId}`
      : buildPersonProfilePath(person.name, person.lastName, person.id);
  const photoSrc = resolveProfilePictureDisplayUrl(person.id, undefined, {
    photoUpdatedAt: person.photoUpdatedAt,
  });

  return (
    <article
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative flex items-center gap-3 rounded border border-gray-200 bg-white py-4 pl-10 pr-4 ${isDragging ? "z-10 shadow-lg" : ""}`}
    >
      <button
        type="button"
        className="absolute left-3 top-3 shrink-0 cursor-grab text-gray-400 active:cursor-grabbing"
        aria-label={`Reorder ${fullName}`}
        {...attributes}
        {...listeners}
      >
        <span className="block h-5 w-5 text-gray-font" aria-hidden>
          <Drag_pan size={20} />
        </span>
      </button>
      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full bg-gray-100">
        <ProfilePhoto src={photoSrc} alt={fullName} />
      </div>
      <div className="min-w-0 flex-1">
        {profilePath ? (
          <Link
            to={profilePath}
            className="font-roboto text-[20px] font-semibold not-italic leading-normal text-Default hover:underline"
          >
            {fullName}
          </Link>
        ) : (
          <p className="m-0 font-roboto text-[20px] font-semibold not-italic leading-normal text-Default">
            {fullName}
          </p>
        )}
        {person.title ? (
          <p className="m-0 font-roboto text-[16px] font-normal not-italic leading-[130%] text-gray-font">
            {person.title}
          </p>
        ) : null}
        {person.email ? (
          <a
            href={`mailto:${person.email}`}
            className="mt-1 flex items-center gap-2 font-roboto text-[14px] font-normal not-italic leading-[130%] text-gray-font hover:underline"
          >
            <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#0070CD] text-white [&_svg]:h-3 [&_svg]:w-3">
              <Email />
            </span>
            {person.email}
          </a>
        ) : null}
      </div>
      <button
        type="button"
        className="shrink-0 text-gray-font hover:text-primary"
        aria-label={`Remove ${fullName}`}
        onClick={() => onRemove(person.id)}
      >
        <Trash className="h-4 w-4" />
      </button>
    </article>
  );
}

const buttonIcon = (icon: ReactElement) => (
  <span className="block h-5 w-5 overflow-hidden [&_svg]:h-5 [&_svg]:w-5 [&_svg]:fill-current">{icon}</span>
);

const actionButtonClass =
  "!h-auto !min-h-0 !w-[200px] !shrink-0 !justify-center !gap-2 !rounded !border !border-solid !py-[10px] !pl-5 !pr-4 !leading-5 !outline-none";

export default function WsgContactStaff(): ReactElement {
  const navigate = useNavigate();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const [staff, setStaff] = useState<WsgContactAdminCard[]>([]);
  const [savedStaff, setSavedStaff] = useState<WsgContactAdminCard[]>([]);
  const [available, setAvailable] = useState<WsgContactAdminCard[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const dirty = useMemo(() => !sameOrder(staff, savedStaff), [savedStaff, staff]);

  useEffect(() => {
    setPageMeta({
      title: "Contacts",
      breadcrumbLabels: {
        "/site-updates": "Contacts",
        "/site-updates/contacts": "Contacts",
      },
    });
    return () => clearPageMeta();
  }, [clearPageMeta, setPageMeta]);

  const applyResponse = (data: { staff: WsgContactAdminCard[]; available: WsgContactAdminCard[] }) => {
    setStaff(data.staff ?? []);
    setSavedStaff(data.staff ?? []);
    setAvailable(data.available ?? []);
  };

  useEffect(() => {
    let cancelled = false;
    getWsgContactAdmin()
      .then((data) => {
        if (!cancelled) {
          applyResponse(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    setStaff((current) => {
      const from = current.findIndex((person) => person.id === active.id);
      const to = current.findIndex((person) => person.id === over.id);
      if (from < 0 || to < 0) {
        return current;
      }
      return arrayMove(current, from, to);
    });
  };

  const addPerson = (person: WsgContactAdminCard) => {
    setStaff((current) => [...current, person]);
    setAvailable((current) => current.filter((item) => item.id !== person.id));
    setPickerOpen(false);
  };

  const removePerson = (id: string) => {
    const person = staff.find((item) => item.id === id);
    setStaff((current) => current.filter((item) => item.id !== id));
    if (person) {
      setAvailable((current) =>
        [...current, person].sort((left, right) =>
          `${left.lastName} ${left.name}`.localeCompare(`${right.lastName} ${right.name}`),
        ),
      );
    }
  };

  const save = async () => {
    if (saving) {
      return;
    }
    if (!dirty) {
      toastSuccess("WSG Staff contacts saved.");
      return;
    }
    setSaving(true);
    try {
      const data = await saveWsgContactAdmin(staff.map((person) => person.id));
      applyResponse(data);
      toastSuccess("WSG Staff contacts saved.");
    } catch {
      toastError("The contact list could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <ContactTopicCatalog />
      <section className="flex w-full flex-col items-start gap-6 self-stretch bg-white p-6">
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="m-0 font-roboto text-2xl font-bold not-italic leading-normal text-Default">
            WSG Staff
          </h2>
          <div className="relative">
            <Button
              text="Add New WSG Staff"
              color="secondary"
              variant="filled"
              size="fixed"
              icon={buttonIcon(<User />)}
              iconPosition="right"
              disabled={loading || available.length === 0}
              onClick={() => setPickerOpen((open) => !open)}
            />
            {pickerOpen && available.length > 0 ? (
              <ul className="absolute right-0 z-20 mt-2 max-h-80 w-72 overflow-y-auto rounded border border-gray-200 bg-white shadow-lg">
                {available.map((person) => (
                  <li key={person.id}>
                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-gray-font hover:bg-gray-50"
                      onClick={() => addPerson(person)}
                    >
                      {person.name} {person.lastName}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        {loading ? <p className="text-sm text-gray-font">Loading staff...</p> : null}
        {failed ? <p className="text-sm text-gray-font">WSG Staff could not be loaded.</p> : null}
        {!loading && !failed && staff.length === 0 ? (
          <p className="text-sm text-gray-font">No WSG Staff contacts are on the public page yet.</p>
        ) : null}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={staff.map((person) => person.id)} strategy={rectSortingStrategy}>
            <div className="grid w-full gap-[30px] md:grid-cols-2 xl:grid-cols-3">
              {staff.map((person) => (
                <StaffCard key={person.id} person={person} onRemove={removePerson} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </section>

      <div className="flex justify-end gap-3 bg-transparent">
        <Button
          text="Cancel"
          color="neutral"
          variant="outlined"
          size="fixed"
          icon={buttonIcon(<Cancel />)}
          iconPosition="right"
          className={`${actionButtonClass} !border-gray-font !bg-transparent !text-gray-font hover:!border-neutral-dark hover:!bg-neutral-dark hover:!text-white`}
          onClick={() => navigate("/contact")}
        />
        <Button
          text="Save"
          color="secondary"
          variant="filled"
          size="fixed"
          icon={buttonIcon(<SaveChanges />)}
          iconPosition="right"
          className={`${actionButtonClass} !border-Default !bg-Default !text-white hover:!border-accent-Default-dark hover:!bg-accent-Default-dark`}
          onClick={save}
        />
      </div>
    </div>
  );
}
